import cx from "classnames";
import find from "lodash/find";
import React, {
  DetailedHTMLProps,
  HTMLAttributes,
  PropsWithChildren,
} from "react";
import { match, P } from "ts-pattern";

import { ConditionOffer } from "shared/product/ProductPageContext";
import { Variant } from "shared/product/ProductSection/product-section.helper";
import { formatToCurrency } from "utils/price.utils";
import {
  usePriceAdvantage,
  UsePriceManagementProps,
} from "utils/usePriceAdvantage";
import { useI18n } from "utils/with-i18n.utils";

import Promo from "./promo.svg";

export type Condition = {
  offer: ConditionOffer;
  label: string;
  variants: Variant[];
};

type DeviceConditionProps = {
  condition: Condition;
  isSelected?: boolean;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const getLowestPricesFromVariants = (
  variants: Variant[],
): UsePriceManagementProps => {
  return variants.reduce<UsePriceManagementProps>(
    (acc, variant) => {
      if (
        variant.originalUpfrontPrice !== null &&
        (acc.originalUpfrontPrice === null ||
          variant.originalUpfrontPrice < acc.originalUpfrontPrice)
      ) {
        acc.originalUpfrontPrice = variant.originalUpfrontPrice;
      }

      if (variant.upfrontPrice < acc.upfrontPrice) {
        acc.upfrontPrice = variant.upfrontPrice;
      }

      if (
        variant.originalSubscriptionPrice !== null &&
        (acc.originalSubscriptionPrice === null ||
          variant.originalSubscriptionPrice < acc.originalSubscriptionPrice)
      ) {
        acc.originalSubscriptionPrice = variant.originalSubscriptionPrice;
      }

      if (variant.subscriptionPrice < acc.subscriptionPrice) {
        acc.subscriptionPrice = variant.subscriptionPrice;
      }

      return acc;
    },
    {
      originalUpfrontPrice: null,
      upfrontPrice: Infinity,
      originalSubscriptionPrice: null,
      subscriptionPrice: Infinity,
    },
  );
};

export const DeviceCondition = ({
  condition,
  isSelected = false,
  ...props
}: PropsWithChildren<DeviceConditionProps>) => {
  const i18n = useI18n();

  const {
    originalUpfrontPrice,
    upfrontPrice,
    originalSubscriptionPrice,
    subscriptionPrice,
  } = getLowestPricesFromVariants(condition.variants);

  const { hasSeniorityDiscount, smartphoneFamilyHasSubscriptionPriceDrop } =
    usePriceAdvantage({
      originalUpfrontPrice,
      upfrontPrice,
      originalSubscriptionPrice,
      subscriptionPrice,
    });

  const subscriptionPriceFormatted = formatToCurrency(
    "fr-FR",
    "EUR",
    subscriptionPrice,
    undefined,
    false,
    false,
  );

  const hasVariantInStock = condition.variants.some(
    (variant) => variant.isInStock,
  );

  const isSelectedConditionOfferInStock = !!find(condition.variants, {
    isInStock: true,
  });

  const priceLabelOptions = match({
    hasSeniorityDiscount,
    isSelectedConditionOfferInStock,
    smartphoneFamilyHasSubscriptionPriceDrop,
    subscriptionPriceFormatted,
    hasVariantInStock,
  })
    .with(
      {
        hasSeniorityDiscount: true,
        isSelectedConditionOfferInStock: true,
        hasVariantInStock: true,
      },
      () => ({
        conditionPrice: subscriptionPriceFormatted,
        priceStyle: "lg:text-lg text-indigo-400",
      }),
    )
    .with(
      {
        smartphoneFamilyHasSubscriptionPriceDrop: true,
        isSelectedConditionOfferInStock: true,
        hasVariantInStock: true,
      },
      () => ({
        conditionPrice: subscriptionPriceFormatted,
        priceStyle: "lg:text-lg text-indigo-400",
      }),
    )
    .with(
      {
        hasSeniorityDiscount: false,
        smartphoneFamilyHasSubscriptionPriceDrop: P.nullish,
        isSelectedConditionOfferInStock: true,
        hasVariantInStock: true,
      },
      () => ({
        conditionPrice: subscriptionPriceFormatted,
        isSelectedConditionOfferInStock: false,
        priceStyle: "lg:text-lg",
      }),
    )
    .otherwise(() => ({
      priceStyle: "lg:text-lg",
    }));

  const priceLabelKey = match({
    subscriptionPriceFormatted,
    isSelectedConditionOfferInStock,
    hasVariantInStock,
  })
    .with(
      {
        subscriptionPriceFormatted: P.not(P.nullish),
        hasVariantInStock: true,
        isSelectedConditionOfferInStock: true,
      },
      () => "product.conditionSelector.price",
    )
    .otherwise(() => "product.conditionSelector.unavailable");

  const priceLabel =
    priceLabelKey &&
    priceLabelOptions &&
    i18n.t(priceLabelKey, priceLabelOptions, {
      html: true,
    });

  return (
    <div
      key={condition.offer}
      className={cx(
        "relative py-2 sticky-lg:px-6 px-4 rounded-lg border-2 max-w-[200px] cursor-pointer",
        isSelected
          ? "bg-white border-black border-2 border-insite"
          : "border-black/35",
      )}
      {...props}
    >
      {(smartphoneFamilyHasSubscriptionPriceDrop || hasSeniorityDiscount) && (
        <div className="absolute top-[-26px] left-1 font-bold rotate-[1deg]">
          <Promo />
        </div>
      )}

      <div className={cx(!hasVariantInStock && "opacity-50")}>
        {!hasVariantInStock && (
          <div className="absolute top-0 left-0 z-20 w-full h-full bg-brown/15" />
        )}

        <div className="text-center">
          <div className="font-semibold lg:text-lg">{condition.label}</div>
          {priceLabel}
        </div>
      </div>
    </div>
  );
};
